import React, {useEffect} from 'react';
import {Button, Divider, FormInstance, Input, Popconfirm, Spin, Typography} from 'antd';
import {
  ProForm,
  ProFormDependency,
  ProFormRadio,
  ProFormSelect,
  ProFormText,
  ProFormTextArea
} from '@ant-design/pro-form';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import {EXTRA_FIELD_VALUE_TYPES} from './types';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {ICON_MAP, TRANSLATION_MAP} from '@/components/ExtraFields/ExtraFieldsList';
import styles from './index.less';
import {ExtraFieldTypeModel, ExtraFieldTypeOptionModel} from '@/typings/models/ExtraFields';
import {
  createExtraFieldTypeOptionRequest,
  deleteExtraFieldTypeOptionRequest,
  editExtraFieldTypeOptionRequest,
} from '@/services/api/extraFieldType';
import {useIntl} from 'umi';
import {processAPIResponseNow} from '@/utils/request';
import isEmpty from 'lodash/isEmpty';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';

interface Props {
  editingField: ExtraFieldTypeModel | null;
  setEditingField: (field: ExtraFieldTypeModel) => void;
  onFormReady: (form: FormInstance) => void;
  onEdit?: () => void;
  isNew: boolean;
}

const RELATED_TO_ENTITY_MESSAGE_IDS: Record<string, string> = {
  result: 'pages.extraFieldType.relatedToResult',
  customer: 'pages.extraFieldType.relatedToCustomer',
  serviceRequest: 'entity.appointment.singular',
  branch: 'entity.branch.singular',
  branchService: 'entity.service.singular',
  serviceType: 'entity.serviceType.singular',
};

const ExtraFieldForm: React.FC<Props> = ({
                                           editingField,
                                           onFormReady,
                                           setEditingField,
                                           onEdit,
                                           isNew,
                                         }) => {
  if (!editingField) return null;
  const intl = useIntl();
  const [form] = ProForm.useForm();
  const [optionsLoading, setOptionsLoading] = React.useState<boolean>(false);
  useEffect(() => {
    onFormReady(form);
  }, [form]);
  const VALUE_TYPE_OPTIONS = Object.entries(EXTRA_FIELD_VALUE_TYPES).map(([key, value]) => ({
    label: <div className={styles.valueTypeOption}>
      <span>{ICON_MAP[value]({})}</span>
      <div>{TRANSLATION_MAP(intl)[value]}</div>
    </div>,
    value,
  }));
  // Separate creation and edit logic for better maintainability
  const createNewOption = async (tempUuid: string, value: string) => {
    if (isEmpty(value)) return;

    try {
      const { data } = await createExtraFieldTypeOptionRequest({
        params: {
          extraFieldTypeId: editingField.uuid,
        },
        data: { value },
      });

      const currentOptions = form.getFieldValue('options') || [];
      const index = currentOptions.findIndex((o: ExtraFieldTypeOptionModel) => o.uuid === tempUuid);
      if (index !== -1) {
        const newOptions = [...currentOptions];
        newOptions[index] = {
          ...newOptions[index],
          uuid: data.data.uuid,
          value: value,
          isNew: false,
        };
        form.setFieldsValue({ options: newOptions });
      }
    } catch (error) {
      console.error('Error creating option:', error);
    }
  };

  const updateExistingOption = async (option: ExtraFieldTypeOptionModel, value: string) => {
    try {
      await editExtraFieldTypeOptionRequest({
        params: {
          extraFieldTypeOptionId: option.uuid,
        },
        data: { value },
      });
    } catch (error) {
      console.error('Error updating option:', error);
    }
  };

  const updateLocalOption = (uuid: string, value: string) => {
    const options = form.getFieldValue('options') || [];
    const index = options.findIndex((o: ExtraFieldTypeOptionModel) => o.uuid === uuid);
    if (index !== -1) {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], value };
      form.setFieldsValue({ options: newOptions });
    }
  };

  const handleOptionLabelChange = (currentOption: ExtraFieldTypeOptionModel, index: number, value: string) => {
    if (currentOption.value === value) return;

    if (isNew) {
      updateLocalOption(currentOption.uuid, value);
      return;
    }

    if (currentOption.isNew) {
      createNewOption(currentOption.uuid, value);
      return;
    }

    updateExistingOption(currentOption, value);
    updateLocalOption(currentOption.uuid, value);
  };

  const handleAddOption = async () => {
    const options = form.getFieldValue('options') || [];
    const newOptions = [...(options || []), {
      value: '',
      isNew: true,
      uuid: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    }];
    form.setFieldsValue({ options: newOptions });
  };

  // Separate deletion logic for better maintainability
  const deleteExistingOption = async (option: ExtraFieldTypeOptionModel, index: number) => {
    const options = form.getFieldValue('options') || [];
    setOptionsLoading(true);

    try {
      const { response, data } = await deleteExtraFieldTypeOptionRequest({
        params: {
          extraFieldTypeOptionId: option.uuid,
        },
      });

      if (response.status > 300) {
        processAPIResponseNow({ response, data });
        return false;
      }

      removeOptionFromForm(index);
      return true;
    } catch (error) {
      console.error('Error deleting option:', error);
      return false;
    } finally {
      setOptionsLoading(false);
    }
  };

  const removeOptionFromForm = (index: number) => {
    const options = form.getFieldValue('options') || [];
    form.setFieldsValue({ options: options.filter((_, i) => i !== index) });
  };

  const handleDeleteOption = async (index: number) => {
    const options = form.getFieldValue('options') || [];
    const option = options[index];

    // If it's a new field or a new option, just remove from form
    if (isNew || option.isNew) {
      removeOptionFromForm(index);
      return;
    }

    // Otherwise, delete from server first
    await deleteExistingOption(option, index);
  };

  const textFieldProps = !isNew ? { onBlur: onEdit } : {};
  const selectFieldProps = !isNew ? { onChange: onEdit, variant: 'underlined' } : { variant: 'underlined' };

  const getRelatedToEntityLabel = (relatedTo: string) => {
    const messageId = RELATED_TO_ENTITY_MESSAGE_IDS[relatedTo] ?? 'pages.extraFieldType.relatedToResult';
    return intl.formatMessage({ id: messageId });
  };
  return (
    <ProForm
      initialValues={editingField}
      layout="vertical"
      form={form}
      submitter={false}
    >
      <ProFormText
        fieldProps={textFieldProps}
        name="label"
        label={intl.formatMessage({
          id: 'common.attribute.label',
          defaultMessage: 'Label',
        })}
        rules={[{ required: true }]}
      />
      <ProFormText
        fieldProps={{ ...textFieldProps }}
        readonly={!isNew}
        name="slug"
        label="Slug"
        render={(dom, entity) => {
          return (
            <Typography.Text code copyable={{ text: entity.value }}>
              {entity.value}
            </Typography.Text>
          );
        }}
      />
      <ProFormTextArea
        fieldProps={textFieldProps}
        name="description"
        label={intl.formatMessage({
          id: 'common.attribute.description',
          defaultMessage: 'Description',
        })}
      />
      <ProFormRadio.Group
        fieldProps={selectFieldProps}
        readonly={!isNew}
        name="related_to"
        label={intl.formatMessage({
          id: 'pages.extraFieldType.relatedTo',
          defaultMessage: 'Related to',
        })}
        options={[
          {
            label: intl.formatMessage({
              id: 'pages.extraFieldType.relatedToResult',
              defaultMessage: 'Result',
            }),
            value: 'result',
          },
          {
            label: intl.formatMessage({
              id: 'pages.extraFieldType.relatedToCustomer',
              defaultMessage: 'Customer',
            }),
            value: 'customer',
          },
          {
            label: intl.formatMessage({
              id: 'entity.appointment.singular',
              defaultMessage: 'Appointment',
            }),
            value: 'serviceRequest',
          },
          {
            label: intl.formatMessage({
              id: 'entity.branch.singular',
              defaultMessage: 'Branch',
            }),
            value: 'branch',
          },
          {
            label: intl.formatMessage({
              id: 'entity.service.singular',
              defaultMessage: 'Branch Service',
            }),
            value: 'branchService',
          },
          {
            label: intl.formatMessage({
              id: 'entity.serviceType.singular',
              defaultMessage: 'Service Type',
            }),
            value: 'serviceType',
          },
        ]}
      />
      <ProFormDependency name={['related_to']} className={'form-item-no-margin'}>
        {({ related_to: relatedTo }) => (
          <ProForm.Item className={'form-item-no-margin'} name="is_auto_attached" valuePropName="checked">
            <ToggleCard
              title={intl.formatMessage({
                id: 'pages.extraFieldType.isAutoAttached',
                defaultMessage: 'Attach automatically',
              })}
              description={intl.formatMessage(
                { id: 'pages.extraFieldType.isAutoAttached.tooltip' },
                { entity: getRelatedToEntityLabel(relatedTo) },
              )}
              onChange={!isNew ? onEdit : undefined}
            />
          </ProForm.Item>
        )}
      </ProFormDependency>
      <ProForm.Item className={'form-item-no-margin'} name="is_confidential" valuePropName="checked">
        <ToggleCard
          title={intl.formatMessage({
            id: 'pages.extraFieldType.isConfidential',
            defaultMessage: 'Confidential',
          })}
          description={intl.formatMessage({
            id: 'pages.extraFieldType.isConfidential.tooltip',
            defaultMessage: 'Confidential fields are only visible to authorized users',
          })}
          onChange={!isNew ? onEdit : undefined}
        />
      </ProForm.Item>
      <ProFormSelect
        fieldProps={selectFieldProps}
        readonly={!isNew}
        name="field_type"
        label={intl.formatMessage({
          id: 'pages.extraFieldType.fieldType',
          defaultMessage: 'Field Type',
        })}
        allowClear={false}
        options={VALUE_TYPE_OPTIONS}
      />
      <ProForm.Item name={'options'} hidden />
      <Spin spinning={optionsLoading}>
        <ProForm.Item shouldUpdate>
          {({ getFieldValue }) => {
            const fieldType = getFieldValue('field_type');
            const options = getFieldValue('options') || [];
            if (
              ![
                EXTRA_FIELD_VALUE_TYPES.SELECT,
                EXTRA_FIELD_VALUE_TYPES.MULTI_SELECT,
              ].includes(fieldType)
            )
              return null;
            return (
              <>
                <Divider>Options</Divider>
                {options?.map((opt, index) => (
                  <Input
                    defaultValue={opt.value}
                    key={opt.uuid}
                    style={{ marginBottom: 8 }}
                    autoFocus={!!opt.isNew}
                    variant="underlined"
                    onBlur={(e) =>
                      handleOptionLabelChange(opt, index, e.target.value)
                    }
                    placeholder={intl.formatMessage({
                      id: 'pages.extraFieldType.optionValuePlaceholder',
                      defaultMessage: 'Option Value',
                    })}
                    onPressEnter={(e) =>
                      e.currentTarget.value && handleAddOption()
                    }
                    data-submit-on-enter="false"
                    addonAfter={
                      <Popconfirm
                        placement={'topLeft'}
                        onConfirm={() => handleDeleteOption(index)}
                        title={
                          <F
                            entity={INTL_ENTITY.OPTION}
                            action={INTL_ACTION.REMOVE_CONFIRMATION}
                          />
                        }
                      >
                        <Button
                          variant={'filled'}
                          color={'danger'}
                          size="small"
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    }
                  />
                ))}
                <Button
                  style={{ marginTop: 8 }}
                  icon={<PlusOutlined />}
                  block
                  type={'dashed'}
                  variant={'dashed'}
                  onClick={handleAddOption}
                >
                  <F entity={INTL_ENTITY.OPTION} action={INTL_ACTION.ADD} />
                </Button>
              </>
            );
          }}
        </ProForm.Item>
      </Spin>
    </ProForm>
  );
};

export default ExtraFieldForm;
